import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Collapse,
  useMediaQuery,
  Stack,
  Chip,
  Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IoRestaurantOutline, IoResizeOutline, IoLocationOutline } from 'react-icons/io5';

const KitchenDescription = ({ kitchen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Utility function to capitalize the first letter
  const capitalizeFirstLetter = (string) => {
    return string
      ? string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
      : null;
  };

  // Destructure kitchen with default for Description to prevent undefined error
  const {
    Description = "No description available",
    Category,
    Size,
    City,
  } = kitchen || {};

  // Toggle description
  const handleToggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const iconMap = {
    category: <IoRestaurantOutline />,
    size: <IoResizeOutline />,
    city: <IoLocationOutline />,
  };

  // Filter out undefined or empty categories
  const categories = [
    { label: `${capitalizeFirstLetter(Category)} Kitchen`, key: "category" },
    { label: `${capitalizeFirstLetter(Size)} Size`, key: "size" },
    { label: `${City}`, key: "city" },
  ].filter((cat) => cat.label && !cat.label.includes("Null"));

  return (
    <Box
      className="kitchen-details-container"
      sx={{
        p: { xs: 1, sm: 0, md: 0 }, // Reduced padding on mobile
        pt: { xs: 0, sm: 0, md: 0 },
        bgcolor: "background.paper",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header and Chips (Visible on All Views) */}
      <Box className="kitchen-header" sx={{ mt: 0, mb: { xs: 2, md: 2.5 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {categories.map((cat) => (
            <Chip
              key={cat.key}
              icon={iconMap[cat.key]}
              label={cat.label}
              variant="outlined"
              size="small"
              sx={{
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                fontWeight: 500,
                color: 'text.secondary',
                borderColor: 'divider',
                borderRadius: '16px',
                p: '12px',
                '.MuiChip-icon': {
                  color: 'inherit',
                  ml: '8px',
                  mr: '-2px'
                },
              }}
            />
          ))}
        </Stack>
      </Box>

      <Divider
        sx={{ my: { xs: 1, md: 1.5 }, bgcolor: "divider", opacity: 0.5 }}
      />

      {/* Description */}
      <Box className="dropdown-section">
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          className="dropdown-header"
          color="text.primary"
          fontWeight={600}
          sx={{
            mb: { xs: 0.5, md: 1 },
            fontSize: { xs: "1rem", md: "1.25rem" },
          }}
          gutterBottom
        >
          About this kitchen
        </Typography>
        <Box sx={{ position: "relative" }}>
          <Collapse 
            in={showFullDescription} 
            timeout={400} 
            collapsedSize={isMobile ? '76px' : '87px'}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                lineHeight: 1.8,
                fontSize: { xs: "0.875rem", md: "1rem" },
              }}
            >
              {Description}
            </Typography>
          </Collapse>
          {(Description.split("\\n").length > 3 || Description.length > 150) && (
            <Link
              component="button"
              onClick={handleToggleDescription}
              sx={{
                color: theme.palette.primary.main,
                textDecoration: "underline",
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                "&:hover": {
                  color: theme.palette.primary.dark,
                  textDecoration: "underline",
                },
                display: "inline-block",
                mt: 1,
              }}
              aria-label={showFullDescription ? "Show less" : "Show more"}
            >
              {showFullDescription ? "Show less" : "Show More"}
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default KitchenDescription;
